server import { parseInteger } from "../lib/archive-db.js";
import {
  buildLocationMarkersJson,
  buildOpenStreetMapLink,
  formatDate,
  formatNumber,
  renderMarkdown,
  rewriteTelegramMessageLinks,
} from "../lib/format.js";
import { hrefWithQuery } from "../lib/url.js";

<const/page=parseInteger($global.url.searchParams.get("page"), 1)>
<const/debug=false>

<const/result=$global.feedResult || {
  entries: [],
  page,
  pageSize: 30,
  hasPreviousPage: false,
  hasNextPage: false,
}>

<const/newerHref=hrefWithQuery("/", {
  page: result.page - 1,
})>

<const/olderHref=hrefWithQuery("/", {
  page: result.page + 1,
})>

<link rel="stylesheet" href="https://unpkg.com/maplibre-gl@4.7.1/dist/maplibre-gl.css">

<main class="reader">
  <header class="masthead">
    <h1>Vahid Online in English</h1>
    <p class="deck">
      Automated AI translations of Vahid Online reporting. Exported from the
      <a href="https://t.me/VahidOnline" target="_blank" rel="noreferrer">Telegram Channel</a>.
      Original content also available on
      <a href="https://x.com/Vahid/" target="_blank" rel="noreferrer">X/Twitter</a>.
    </p>
  </header>

  <section class="feed">
      <if=$global.feedError>
        <p class="empty">Sorry, the archive is unavailable right now. Please try again in a bit.</p>
      </if>
      <else-if=result.entries.length === 0>
        <p class="empty">No posts found.</p>
      </else-if>
      <else>
        <ol>
          <for|entry| of=result.entries>
            <const/primaryRow=entry.rows.find((row) => (row.text || row.raw_text || "").trim().length > 0) || entry.rows[0]>
            <const/originalText=primaryRow ? (primaryRow.text || primaryRow.raw_text || "") : "">
            <const/englishText=(primaryRow && primaryRow.translation_status === "translated" && primaryRow.translated_text) ? primaryRow.translated_text : "">
            <const/hasEnglishTranslation=Boolean(englishText)>
            <const/renderedEnglishHtml=hasEnglishTranslation
              ? rewriteTelegramMessageLinks(renderMarkdown(englishText), {
                  chatId: primaryRow.chat_id,
                })
              : "">
            <const/renderedOriginalHtml=rewriteTelegramMessageLinks(renderMarkdown(originalText), {
              chatId: primaryRow.chat_id,
            })>
            <const/replyTargetId=primaryRow.reply_to_msg_id_extracted || primaryRow.reply_to_msg_id>
            <const/hasLocalReplyTarget=Boolean(entry.hasLocalReplyTarget)>
            <const/replyHref=hasLocalReplyTarget ? `/messages/${primaryRow.chat_id}/${replyTargetId}` : null>
            <const/fwdChannelId=primaryRow.fwd_channel_id>
            <const/fwdChannelPost=primaryRow.fwd_channel_post>
            <const/fwdLabel=primaryRow.fwd_from_name || (fwdChannelId ? `channel ${fwdChannelId}` : null)>
            <const/fwdHref=(fwdChannelId && fwdChannelPost)
              ? `https://t.me/c/${String(fwdChannelId).startsWith("100") ? String(fwdChannelId).slice(3) : String(fwdChannelId)}/${fwdChannelPost}`
              : null>
            <const/isAlbum=Boolean(entry.groupedId && entry.rows.length > 1)>
            <const/locationItems=entry.locationItems || []>
            <const/locationMapItems=locationItems.map((location) => ({
              ...location,
              mapLink: buildOpenStreetMapLink(location.latitude, location.longitude),
            })).filter((location) => location.mapLink)>
            <const/hasLocationMapItems=Boolean(locationMapItems.length)>
            <const/articleClass=fwdLabel ? "is-forwarded" : "">

            <li class="story">
              <article class=articleClass data-message-card>
                <header>
                  <p class="story-index">
                    <a href=`/messages/${primaryRow.chat_id}/${primaryRow.message_id}`>
                      ${formatDate(primaryRow ? primaryRow.date_utc : null)}
                    </a>
                    <if=hasEnglishTranslation>
                      <span class="story-index-sep">·</span>
                      <button type="button" class="story-view-toggle" data-message-toggle aria-pressed="false">
                        <span data-message-toggle-label>show original</span>
                      </button>
                    </if>
                  </p>
                </header>

                <if=fwdLabel>
                  <p class="context-note forwarded-note">
                    Forwarded from
                    <if=fwdHref>
                      <a href=fwdHref target="_blank" rel="noreferrer">${fwdLabel}</a>
                    </if>
                    <else>${fwdLabel}</else>
                  </p>
                </if>

                <if=replyTargetId>
                  <p class="context-note">
                    Replying to
                    <if=replyHref>
                      <a href=replyHref>message #${replyTargetId}</a>
                    </if>
                    <else>message #${replyTargetId}</else>
                  </p>
                </if>

                <if=primaryRow.reply_quote_text>
                  <blockquote class="embedded-quote">${primaryRow.reply_quote_text}</blockquote>
                </if>

                <if=hasEnglishTranslation>
                  <div class=(fwdLabel ? "story-body forwarded-body" : "story-body") data-body-variant="english">$!{renderedEnglishHtml}</div>
                  <div class=(fwdLabel ? "story-body forwarded-body" : "story-body") data-body-variant="original" hidden>$!{renderedOriginalHtml}</div>
                </if>
                <else>
                  <if=originalText>
                    <div class=(fwdLabel ? "story-body forwarded-body" : "story-body")>$!{renderedOriginalHtml}</div>
                    <p class="translation-note">English translation not available yet — showing original text.</p>
                  </if>
                  <else>
                    <p class="story-body muted">No text body for this post.</p>
                  </else>
                </else>

                <if=entry.rows.some((row) => row.has_media) || hasLocationMapItems>
                  <div class="media-grid" data-gl>
                    <if=hasLocationMapItems>
                      <const/locationMarkersJson=buildLocationMarkersJson(locationMapItems)>
                      <div class="story-map-inline">
                        <div
                          class="story-map-frame"
                          data-maplibre-map
                          data-markers=locationMarkersJson
                          data-interactive="true"
                          data-click-to-activate="true"
                          data-map-label=`Location map for message ${primaryRow.message_id}`>
                        </div>
                      </div>
                    </if>
                    <for|mediaRow| of=entry.rows>
                      <if=mediaRow.has_media>
                        <const/mediaHref=`/api/media/${mediaRow.chat_id}/${mediaRow.message_id}`>
                        <const/hasDownloadedMedia=mediaRow.media_download_status === "downloaded">
                        <const/isVideoMedia=mediaRow.media_type === "video" || (mediaRow.media_mime_type && mediaRow.media_mime_type.startsWith("video/"))>
                        <const/isImageMedia=mediaRow.media_type === "photo" || (mediaRow.media_mime_type && mediaRow.media_mime_type.startsWith("image/"))>
                        <const/isAudioMedia=mediaRow.media_type === "voice" || (mediaRow.media_mime_type && mediaRow.media_mime_type.startsWith("audio/"))>

                        <if=hasDownloadedMedia>
                          <if=isVideoMedia>
                            <button type="button" class="gl-thumb" data-gl-item data-gl-src=mediaHref data-gl-type="video">
                              <video class="inline-media" preload="metadata" src=`${mediaHref}#t=0.1` muted playsinline></video>
                              <span class="gl-play" aria-hidden="true"></span>
                            </button>
                          </if>
                          <else>
                            <if=isImageMedia>
                              <button type="button" class="gl-thumb" data-gl-item data-gl-src=mediaHref data-gl-type="image">
                                <img class="inline-media" src=mediaHref alt=`Media ${mediaRow.message_id}` loading="lazy">
                              </button>
                            </if>
                            <else>
                              <if=isAudioMedia>
                                <audio class="inline-media" controls preload="metadata" src=mediaHref></audio>
                              </if>
                              <else>
                                <p class="media-link"><a href=mediaHref target="_blank" rel="noreferrer">Open downloaded media</a></p>
                              </else>
                            </else>
                          </else>
                        </if>
                        <else>
                          <div class="pending-media-frame">
                            <span>pending</span>
                          </div>
                        </else>
                      </if>
                    </for>
                  </div>
                </if>

                <if=debug>
                  <details class="debug">
                    <summary>Debug record</summary>
                    <dl>
                      <dt>chat_id</dt><dd>${primaryRow.chat_id}</dd>
                      <dt>message_id</dt><dd>${primaryRow.message_id}</dd>
                      <dt>entry_key</dt><dd>${entry.entryKey}</dd>
                      <dt>grouped_id</dt><dd>${entry.groupedId || "—"}</dd>
                      <dt>entry_size</dt><dd>${entry.rows.length}</dd>
                      <dt>views</dt><dd>${formatNumber(primaryRow.views)}</dd>
                      <dt>forwards</dt><dd>${formatNumber(primaryRow.forwards)}</dd>
                      <dt>is_deleted</dt><dd>${primaryRow.is_deleted ? "yes" : "no"}</dd>
                      <dt>translation_status</dt><dd>${primaryRow.translation_status || "—"}</dd>
                      <dt>translation_provider</dt><dd>${primaryRow.translation_provider || "—"}</dd>
                      <dt>translation_model</dt><dd>${primaryRow.translation_model || "—"}</dd>
                      <dt>media_download_status</dt><dd>${primaryRow.media_download_status || "—"}</dd>
                    </dl>
                  </details>
                </if>
              </article>
            </li>
          </for>
        </ol>
      </else>

      <nav class="pager">
        <if=result.hasPreviousPage>
          <a href=newerHref>← Newer</a>
        </if>
        <else>
          <span class="disabled">← Newer</span>
        </else>

        <if=result.hasNextPage>
          <a href=olderHref>Older →</a>
        </if>
        <else>
          <span class="disabled">Older →</span>
        </else>
      </nav>
    </section>
</main>

<script>
  (function () {
    const MAPLIBRE_URL = "https://unpkg.com/maplibre-gl@4.7.1/dist/maplibre-gl.js";
    const VECTOR_STYLE_URL = "https://tiles.openfreemap.org/styles/liberty";

    // Fetch the vector tile style once, rewrite all text labels to English only.
    let _cachedStylePromise = null;
    const ENGLISH_TEXT_FIELD = ["coalesce", ["get", "name:en"], ["get", "name_en"], ["get", "name:latin"], ["get", "name"]];
    function getEnglishStyle() {
      if (!_cachedStylePromise) {
        _cachedStylePromise = fetch(VECTOR_STYLE_URL)
          .then((r) => r.json())
          .then((style) => {
            for (const layer of style.layers || []) {
              if (layer.layout?.["text-field"]) {
                layer.layout["text-field"] = ENGLISH_TEXT_FIELD;
              }
            }
            return style;
          });
      }
      return _cachedStylePromise;
    }

    function toggleMessageLanguage(event) {
      const toggle = event.target.closest("[data-message-toggle]");
      if (!toggle) return;

      const card = toggle.closest("[data-message-card]");
      if (!card) return;

      const englishBody = card.querySelector('[data-body-variant="english"]');
      const originalBody = card.querySelector('[data-body-variant="original"]');
      if (!englishBody || !originalBody) return;

      const showingEnglish = !englishBody.hidden;
      englishBody.hidden = showingEnglish;
      originalBody.hidden = !showingEnglish;

      toggle.setAttribute("aria-pressed", showingEnglish ? "true" : "false");

      const label = toggle.querySelector("[data-message-toggle-label]");
      if (label) {
        label.textContent = showingEnglish ? "show english" : "show original";
      }
    }

    function setMapInteractionEnabled(map, enabled) {
      const method = enabled ? "enable" : "disable";
      map.dragPan?.[method]?.();
      map.scrollZoom?.[method]?.();
      map.doubleClickZoom?.[method]?.();
      map.touchZoomRotate?.[method]?.();
      map.keyboard?.[method]?.();

      map.dragRotate?.disable?.();
      map.boxZoom?.disable?.();
      map.touchPitch?.disable?.();
    }

    function installClickToActivateOverlay(element, map) {
      const overlay = document.createElement("button");
      overlay.type = "button";
      overlay.className = "story-map-activate";
      overlay.textContent = "Click to interact";

      element.appendChild(overlay);
      element.classList.add("is-map-locked");
      setMapInteractionEnabled(map, false);

      overlay.addEventListener("click", (event) => {
        event.preventDefault();
        event.stopPropagation();

        setMapInteractionEnabled(map, true);
        element.classList.remove("is-map-locked");
        overlay.hidden = true;
      });
    }

    async function createMap(element) {
      if (element.dataset.mapReady === "true") return;

      const markersJson = element.dataset.markers || "";
      let markers = [];
      if (markersJson) {
        try {
          const parsed = JSON.parse(markersJson);
          if (Array.isArray(parsed)) {
            markers = parsed
              .map((marker, index) => {
                const north = Number(marker?.bounds?.north);
                const east = Number(marker?.bounds?.east);
                const south = Number(marker?.bounds?.south);
                const west = Number(marker?.bounds?.west);
                const bounds = [north, east, south, west].every(Number.isFinite)
                  ? { north, east, south, west }
                  : null;

                return {
                  lat: Number(marker?.lat),
                  lon: Number(marker?.lon),
                  label: marker?.label || `Location ${index + 1}`,
                  link: marker?.link || null,
                  precision: marker?.precision || null,
                  locationType: marker?.locationType || null,
                  bounds,
                };
              })
              .filter((marker) => Number.isFinite(marker.lat) && Number.isFinite(marker.lon));
          }
        } catch {
          markers = [];
        }
      }

      if (!markers.length) {
        const lat = Number.parseFloat(element.dataset.lat || "");
        const lon = Number.parseFloat(element.dataset.lon || "");
        if (Number.isFinite(lat) && Number.isFinite(lon)) {
          markers = [{ lat, lon, label: "Location", link: null }];
        }
      }

      if (!markers.length) return;

      const interactive = element.dataset.interactive === "true";
      const clickToActivate = element.dataset.clickToActivate === "true";
      const mapLabel = element.dataset.mapLabel;
      if (mapLabel) {
        element.setAttribute("aria-label", mapLabel);
      }

      // Pick zoom based on what the marker represents.
      // For single markers the precision/type tells us the geographic
      // scale; for multiple markers fitBounds handles it below.
      function zoomForMarker(marker) {
        const p = (marker.precision || "").toLowerCase();
        const t = (marker.locationType || "").toLowerCase();
        const label = (marker.label || "").toLowerCase();

        if (p === "country" || t === "country") return 5;
        if (p === "province" || t === "province" || t === "region") return 7;
        if (/\bstrait\b|\bgulf\b|\bsea\b|\bocean\b|\blake\b|\briver\b|\bmountain\b/.test(label)) return 8;
        if (p === "city" || t === "city") return 11;
        if (p === "district" || t === "district" || t === "neighborhood") return 13;
        if (t === "street" || t === "intersection") return 14;
        if (p === "exact" || t === "facility" || t === "airport" || t === "military base"
            || t === "specific" || t === "specific place" || t === "complex"
            || t === "organization" || t === "base" || t === "coordinates") return 13;
        if (p === "unknown" || p === "") return 10;
        return 11;
      }

      const initialZoom = markers.length === 1 ? zoomForMarker(markers[0]) : 11;

      const style = await getEnglishStyle();

      const map = new window.maplibregl.Map({
        container: element,
        style,
        center: [markers[0].lon, markers[0].lat],
        zoom: initialZoom,
        attributionControl: false,
        interactive: true,
      });

      if (interactive) {
        map.addControl(new window.maplibregl.NavigationControl({ showCompass: false }), "top-right");
      } else {
        setMapInteractionEnabled(map, false);
      }

      if (interactive && clickToActivate) {
        installClickToActivateOverlay(element, map);
      }

      const bounds = new window.maplibregl.LngLatBounds();
      markers.forEach((marker, index) => {
        const popupContent = document.createElement("div");
        popupContent.className = "map-popup";

        const title = document.createElement("strong");
        title.textContent = marker.label;
        popupContent.appendChild(title);

        if (marker.link) {
          const line = document.createElement("div");
          const link = document.createElement("a");
          link.href = marker.link;
          link.target = "_blank";
          link.rel = "noreferrer";
          link.textContent = "Open map";
          line.appendChild(link);
          popupContent.appendChild(line);
        }

        const popup = new window.maplibregl.Popup({ offset: 16, closeButton: false }).setDOMContent(popupContent);

        new window.maplibregl.Marker({ color: "#3b82f6" })
          .setLngLat([marker.lon, marker.lat])
          .setPopup(popup)
          .addTo(map);

        bounds.extend([marker.lon, marker.lat]);
      });

      if (markers.length > 1) {
        map.fitBounds(bounds, {
          padding: 34,
          maxZoom: 14,
          duration: 0,
        });
      } else if (markers[0].bounds) {
        const markerBounds = new window.maplibregl.LngLatBounds(
          [markers[0].bounds.west, markers[0].bounds.south],
          [markers[0].bounds.east, markers[0].bounds.north],
        );
        map.fitBounds(markerBounds, {
          padding: 34,
          maxZoom: 13,
          duration: 0,
        });
      }

      map.once("load", () => map.resize());
      element.dataset.mapReady = "true";
    }

    function ensureMapLibreLoaded(callback) {
      if (window.maplibregl) {
        callback();
        return;
      }

      const existingScript = document.querySelector("script[data-maplibre-loader]");
      if (existingScript) {
        existingScript.addEventListener("load", callback, { once: true });
        return;
      }

      const script = document.createElement("script");
      script.src = MAPLIBRE_URL;
      script.async = true;
      script.dataset.maplibreLoader = "true";
      script.addEventListener("load", callback, { once: true });
      document.head.appendChild(script);
    }

    function initMaps() {
      const elements = Array.from(document.querySelectorAll("[data-maplibre-map]"));
      if (!elements.length) return;

      // Lazy-initialize maps via IntersectionObserver to stay within the
      // browser's WebGL context limit (~8-16).  Maps are created when they
      // enter a generous margin around the viewport and removed when they
      // scroll far away, keeping only a handful of live contexts.
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((ioEntry) => {
            if (ioEntry.isIntersecting) {
              ensureMapLibreLoaded(() => createMap(ioEntry.target));
            } else if (ioEntry.target.dataset.mapReady === "true") {
              destroyMap(ioEntry.target);
            }
          });
        },
        { rootMargin: "200px 0px" },
      );

      elements.forEach((el) => observer.observe(el));
    }

    function destroyMap(element) {
      // Remove the MapLibre instance so its WebGL context is freed.
      const canvas = element.querySelector(".maplibregl-canvas-container");
      if (canvas) canvas.remove();
      // MapLibre adds several sibling elements; simplest to clear everything.
      const ctrl = element.querySelectorAll(".maplibregl-control-container, .maplibregl-cooperative-gesture-screen");
      ctrl.forEach((el) => el.remove());
      element.dataset.mapReady = "false";
    }

    document.addEventListener("click", toggleMessageLanguage);
    initMaps();
  })();
</script>

<style>
  .reader {
    max-width: 900px;
    margin: 0 auto;
    padding: 1.4rem 1rem 2.6rem;
  }

  .masthead {
    border-bottom: 2px solid var(--rule);
    padding-bottom: 0.9rem;
    margin-bottom: 1rem;
  }

  .masthead h1 {
    margin: 0.2rem 0 0;
    font-size: clamp(1.6rem, 3.4vw, 2.4rem);
    font-family: "Source Serif 4", serif;
    font-weight: 600;
  }

  .deck {
    margin: 0.45rem 0 0;
    color: var(--muted);
    max-width: 70ch;
  }

  .feed {
    padding: 0;
  }

  ol {
    margin: 0;
    padding: 0;
    list-style: none;
    display: grid;
    gap: 1.8rem;
  }

  .story {
    border: none;
    padding: 1rem 0;
    background: transparent;
  }

  .story + .story {
    border-top: 1px solid #c4bfb6;
    margin-left: calc(-1rem - ((100vw - 100%) / 2));
    margin-right: calc(-1rem - ((100vw - 100%) / 2));
    padding-left: calc(1rem + ((100vw - 100%) / 2));
    padding-right: calc(1rem + ((100vw - 100%) / 2));
  }

  .story article.is-forwarded {
    border-left: 3px solid #d7cfbf;
    padding-left: 0.85rem;
  }



  .story-index {
    margin: 0 0 0.45rem;
    display: flex;
    justify-content: flex-start;
    align-items: baseline;
    gap: 0.4rem;
    color: var(--muted);
    font-size: 0.77rem;
    font-family: "IBM Plex Mono", monospace;
  }

  .story-index > a {
    color: var(--accent);
    text-decoration: none;
  }

  .story-index > a:hover {
    text-decoration: underline;
  }

  .story-index-sep {
    color: #b5ae9f;
    user-select: none;
  }

  .story-view-toggle {
    border: 0;
    background: transparent;
    padding: 0;
    display: inline-flex;
    align-items: center;
    color: #9b9487;
    font-size: 0.72rem;
    font-family: inherit;
    white-space: nowrap;
    cursor: pointer;
  }

  .story-view-toggle:hover {
    color: #7f786c;
    text-decoration: underline;
  }

  .story-body {
    margin: 0;
    line-height: 1.55;
    font-family: "Source Serif 4", serif;
    font-size: 1.05rem;
    overflow-wrap: anywhere;
  }

  .story-body :where(p, ul, ol, blockquote, pre) {
    margin: 0 0 0.65rem;
  }

  .story-body :where(p:last-child, ul:last-child, ol:last-child, blockquote:last-child, pre:last-child) {
    margin-bottom: 0;
  }

  .story-body pre {
    background: #f6f2ea;
    border: 1px solid var(--rule);
    padding: 0.55rem;
    overflow: auto;
    font-size: 0.88rem;
    line-height: 1.42;
  }

  .story-body code {
    font-family: "IBM Plex Mono", monospace;
    font-size: 0.88em;
  }

  .story-body blockquote {
    border-left: 3px solid var(--rule);
    padding-left: 0.65rem;
    color: var(--muted);
  }

  .muted {
    color: var(--muted);
    font-style: italic;
  }

  .context-note {
    margin: 0 0 0.45rem;
    color: var(--muted);
    font-family: "IBM Plex Mono", monospace;
    font-size: 0.74rem;
  }

  .forwarded-note {
    margin-bottom: 0.35rem;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    font-size: 0.69rem;
  }

  .forwarded-body {
    background: #f8f4eb;
    border: 1px solid #e6ddcd;
    padding: 0.62rem 0.74rem;
  }

  .embedded-quote {
    margin: 0 0 0.65rem;
    padding: 0.5rem 0.65rem;
    border-left: 3px solid var(--rule);
    background: #f7f4ee;
    color: #3d3932;
    font-size: 0.95rem;
    line-height: 1.45;
  }

  .translation-note {
    margin: 0.45rem 0 0;
    color: var(--muted);
    font-family: "IBM Plex Mono", monospace;
    font-size: 0.74rem;
  }

  .story-map-inline {
    flex: 0 0 auto;
    width: 300px;
    max-width: 100%;
    border: 1px solid var(--rule);
    background: #fff;
    overflow: hidden;
  }

  .story-map-frame :global(.maplibregl-popup-content) {
    font-family: "IBM Plex Mono", monospace;
    font-size: 0.72rem;
    padding: 0.38rem 0.45rem;
  }

  .story-map-frame :global(.map-popup) {
    display: grid;
    gap: 0.18rem;
  }

  .story-map-head {
    display: flex;
    justify-content: space-between;
    gap: 0.5rem;
    align-items: baseline;
    margin-bottom: 0.4rem;
    font-family: "IBM Plex Mono", monospace;
    font-size: 0.72rem;
    color: var(--muted);
  }

  .story-map-frame {
    width: 100%;
    height: 220px;
    border: 0;
    display: block;
    position: relative;
    overflow: hidden;
  }

  .story-map-activate {
    position: absolute;
    inset: 0;
    border: 0;
    margin: 0;
    padding: 0;
    background: rgba(17, 17, 17, 0.24);
    color: #fff;
    font-family: "IBM Plex Mono", monospace;
    font-size: 0.7rem;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    cursor: pointer;
    z-index: 6;
  }

  .story-map-frame.is-map-locked :global(.maplibregl-canvas) {
    cursor: pointer;
  }

  .story-map-frame :global(.maplibregl-ctrl-top-right) {
    top: 0.35rem;
    right: 0.35rem;
  }

  .story-map-frame :global(.maplibregl-ctrl-group) {
    transform: scale(0.84);
    transform-origin: top right;
  }



  .media-grid {
    margin-top: 0.55rem;
    display: flex;
    flex-wrap: wrap;
    gap: 0.55rem;
    align-items: flex-start;
    justify-content: flex-start;
  }

  .media-grid > * {
    flex: 0 0 auto;
  }

  .media-direct-link {
    display: inline-block;
    text-decoration: none;
  }

  .inline-media {
    display: block;
    width: auto;
    height: 220px;
    max-width: min(100%, 560px);
    object-fit: contain;
    border: 1px solid var(--rule);
    background: #fff;
    cursor: pointer;
  }

  audio.inline-media {
    width: min(100%, 320px);
    height: auto;
    max-height: none;
    padding: 0.25rem;
  }

  .media-link {
    margin: 0.5rem 0 0;
    font-family: "IBM Plex Mono", monospace;
    font-size: 0.76rem;
  }

  .pending-media-frame {
    width: min(100%, 320px);
    height: 220px;
    border: 1px dashed var(--rule);
    background: #f8f5ef;
    display: grid;
    place-items: center;
    color: var(--muted);
    font-family: "IBM Plex Mono", monospace;
    font-size: 0.82rem;
    text-transform: lowercase;
  }

  .debug {
    margin-top: 0.65rem;
    font-family: "IBM Plex Mono", monospace;
    font-size: 0.78rem;
  }

  .debug summary {
    cursor: pointer;
    color: var(--muted);
  }

  .debug dl {
    margin: 0.45rem 0 0;
    display: grid;
    grid-template-columns: max-content 1fr;
    gap: 0.3rem 0.6rem;
  }

  .debug dt {
    color: var(--muted);
  }

  .debug dd {
    margin: 0;
  }

  .pager {
    margin-top: 0.8rem;
    padding-top: 0.7rem;
    border-top: 1px solid var(--rule);
    display: flex;
    justify-content: space-between;
    font-family: "IBM Plex Mono", monospace;
    font-size: 0.82rem;
  }

  .disabled {
    color: #aea594;
  }

  .empty {
    color: var(--muted);
    margin: 0.7rem 0 0;
  }


</style>
